// ===== Start: Root response field configuration =====
// Defines which top-level crisis fields can be updated in patch mode.
const ROOT_PATCH_FIELDS = [
  'id',
  'name',
  'reference_number',
  'id_tenant',
  'units_users_rh',
  'csis_days_in_field',
  'csis_total_volunteers',
  'description',
  'level',
  'to_list_id',
  'cc_list_id',
  'bcc_list_id',
  'last_status',
  'validations'
];
// ===== End: Root response field configuration =====

// ===== Start: Shared response helpers =====
// Keeps partial API responses from overwriting existing values with undefined.
const hasResponseField = (response, field) => Object.prototype.hasOwnProperty.call(response || {}, field);

const getExistingField = (object, field) => (
  object && Object.prototype.hasOwnProperty.call(object, field) ? object[field] : undefined
);

const firstExistingValue = (...values) => values.find(value => value !== undefined);

const normalizeSupervisorIds = (supervisors = []) => supervisors.map(supervisor => (
  typeof supervisor === 'object' ? supervisor.id : supervisor
));

const hasValue = value => value !== null && value !== undefined;
// ===== End: Shared response helpers =====

// ===== Start: Emergency generator value mapping =====
// Converts affect unit pivot values into the fields used by the form.
const getEmergencySystemsValue = (affects = [], type) => {
  if (!Array.isArray(affects) || !affects.length) {
    return 0;
  }

  const emergencySystemValue = {
    has_emergency_generator: 14,
    emergency_generator_capacity: 15,
    emergency_generator_diesel_level: 16,
  };
  const value = affects.find(affect => affect.id === emergencySystemValue[type]);

  return value ? value.pivot.value : 0;
};
// ===== End: Emergency generator value mapping =====

// ===== Start: Unit normalization =====
// Normalizes full and partial unit payloads into the local crisis unit shape.
const normalizeCrisisUnit = (unit, currentUnit = {}) => {
  const crisisUnit = unit.operational_security_crisis_unit || unit.unit?.operational_security_crisis_unit || unit;
  const unitData = crisisUnit.unit || unit.unit || unit;
  const affectUnits = Array.isArray(crisisUnit.affect_units) ? crisisUnit.affect_units : null;
  // Detail responses can be partial, so missing fields keep the current unit value.
  const getPivotField = field => firstExistingValue(
    getExistingField(crisisUnit, field),
    getExistingField(currentUnit, field)
  );
  const getUnitField = field => firstExistingValue(
    getExistingField(unitData, field),
    getExistingField(crisisUnit, field),
    getExistingField(currentUnit, field)
  );

  return {
    id: getPivotField('id'),
    id_unit: firstExistingValue(
      getExistingField(crisisUnit, 'id_unit'),
      getExistingField(unitData, 'id'),
      getExistingField(unit, 'id_unit'),
      getExistingField(currentUnit, 'id_unit')
    ),
    id_operational_security_crisis: getPivotField('id_operational_security_crisis'),
    description: getUnitField('description'),
    municipality: getUnitField('municipality'),
    region: getUnitField('region'),
    sirh: getUnitField('sirh'),
    rems: getUnitField('rems'),
    operation_impact: getPivotField('operation_impact'),
    out_off_comunication: getPivotField('out_off_comunication'),
    affectsUnits: affectUnits
      ? affectUnits.map(au => au.id)
      : firstExistingValue(getExistingField(crisisUnit, 'affectsUnits'), currentUnit.affectsUnits, []),
    users_rh: firstExistingValue(
      getExistingField(unitData, 'users_rh'),
      getExistingField(crisisUnit, 'users_rh'),
      currentUnit.users_rh,
      []
    ),
    last_risk_evaluation: getUnitField('last_risk_evaluation'),
    category: getUnitField('category'),
    at_risk: getPivotField('at_risk'),
    damaged_cameras: getPivotField('damaged_cameras'),
    affected_amount: getPivotField('affected_amount'),
    blocked_roads: getPivotField('blocked_roads'),
    blocked_roads_comments: getPivotField('blocked_roads_comments'),
    sirh_nearest_branch: getPivotField('sirh_nearest_branch'),
    has_emergency_generator: affectUnits
      ? getEmergencySystemsValue(affectUnits, 'has_emergency_generator')
      : getPivotField('has_emergency_generator'),
    emergency_generator_capacity: affectUnits
      ? getEmergencySystemsValue(affectUnits, 'emergency_generator_capacity')
      : getPivotField('emergency_generator_capacity'),
    emergency_generator_diesel_level: affectUnits
      ? getEmergencySystemsValue(affectUnits, 'emergency_generator_diesel_level')
      : getPivotField('emergency_generator_diesel_level'),
    errors: firstExistingValue(getExistingField(crisisUnit, 'errors'), currentUnit.errors) || {
      operation_impact: null,
      out_off_comunication: null,
      affectsUnits: null
    }
  };
};
// ===== End: Unit normalization =====

// ===== Start: Unit matching and patch extraction =====
// Finds the local unit affected by a backend response without requiring full data.
const unitMatches = (currentUnit, updatedUnit) => {
  if (hasValue(updatedUnit.id) && hasValue(currentUnit.id) && String(updatedUnit.id) === String(currentUnit.id)) {
    return true;
  }

  if (hasValue(updatedUnit.id_unit) && hasValue(currentUnit.id_unit) && String(updatedUnit.id_unit) === String(currentUnit.id_unit)) {
    return true;
  }

  return updatedUnit.sirh && currentUnit.sirh && String(updatedUnit.sirh) === String(currentUnit.sirh);
};

const extractPatchUnits = (units = []) => units.flatMap((unit) => {
  // Some endpoints wrap the updated unit inside a response-like object.
  if (Array.isArray(unit?.units)) {
    return unit.units;
  }

  if (Array.isArray(unit?.data?.units)) {
    return unit.data.units;
  }

  return unit ? [unit] : [];
});
// ===== End: Unit matching and patch extraction =====

export function useCrisisResponsePatch({ operationalSecurityCrisis, tenantChanged }) {
  // ===== Start: Unit patching =====
  // Applies only returned units and preserves all other unit objects and order.
  const patchCrisisUnits = (units = []) => {
    const patchUnits = extractPatchUnits(units);

    if (!patchUnits.length) {
      return;
    }

    const currentUnits = [...operationalSecurityCrisis.value.units];

    patchUnits.forEach((rawUnit) => {
      // First normalize only identifiers to find the existing unit safely.
      const unitMatcher = normalizeCrisisUnit(rawUnit);
      const unitIndex = currentUnits.findIndex(currentUnit => unitMatches(currentUnit, unitMatcher));

      if (unitIndex >= 0) {
        // The second normalization merges the partial payload with the current unit.
        const updatedUnit = normalizeCrisisUnit(rawUnit, currentUnits[unitIndex]);

        currentUnits.splice(unitIndex, 1, updatedUnit);
      }
    });

    operationalSecurityCrisis.value.units = currentUnits;
  };
  // ===== End: Unit patching =====

  // ===== Start: Crisis patch response handling =====
  // Updates only root fields and units that are present in the response.
  const patchOperationalSecurityCrisisFromResponse = (response) => {
    if (!response) {
      return;
    }

    ROOT_PATCH_FIELDS.forEach((field) => {
      if (hasResponseField(response, field)) {
        operationalSecurityCrisis.value[field] = response[field];
      }
    });

    if (hasResponseField(response, 'id_tenant')) {
      tenantChanged(response.id_tenant);
    }

    if (hasResponseField(response, 'supervisors')) {
      operationalSecurityCrisis.value.supervisors = normalizeSupervisorIds(response.supervisors);
    }

    if (Array.isArray(response.units)) {
      patchCrisisUnits(response.units);
    }
  };
  // ===== End: Crisis patch response handling =====

  // ===== Start: Crisis replace response handling =====
  // Rebuilds the full crisis state for initial load and structural refreshes.
  const replaceOperationalSecurityCrisisFromResponse = (response) => {
    const {
      id,
      name,
      description,
      level,
      csis_days_in_field,
      csis_total_volunteers,
      to_list_id,
      cc_list_id,
      bcc_list_id,
      reference_number,
      id_tenant,
      units = [],
      units_users_rh = [],
      supervisors = [],
      last_status,
      validations = []
    } = response;

    operationalSecurityCrisis.value.id = id;
    operationalSecurityCrisis.value.name = name;
    operationalSecurityCrisis.value.reference_number = reference_number;
    operationalSecurityCrisis.value.id_tenant = id_tenant;
    operationalSecurityCrisis.value.units_users_rh = units_users_rh;
    operationalSecurityCrisis.value.csis_days_in_field = csis_days_in_field;
    operationalSecurityCrisis.value.csis_total_volunteers = csis_total_volunteers;
    operationalSecurityCrisis.value.description = description;
    operationalSecurityCrisis.value.level = level;
    operationalSecurityCrisis.value.to_list_id = to_list_id;
    operationalSecurityCrisis.value.cc_list_id = cc_list_id;
    operationalSecurityCrisis.value.bcc_list_id = bcc_list_id;
    operationalSecurityCrisis.value.last_status = last_status;
    operationalSecurityCrisis.value.validations = validations;

    tenantChanged(id_tenant);

    operationalSecurityCrisis.value.units = units && units.length ? units.map(unit => normalizeCrisisUnit(unit)) : [];
    operationalSecurityCrisis.value.supervisors = normalizeSupervisorIds(supervisors);
  };
  // ===== End: Crisis replace response handling =====

  // ===== Start: Public response dispatcher =====
  // Chooses patch or replace behavior based on the caller option.
  const buildOperationalSecurityCrisisFromResponse = (response, options = {}) => {
    if (options.mode === 'patch') {
      patchOperationalSecurityCrisisFromResponse(response);
      return;
    }

    replaceOperationalSecurityCrisisFromResponse(response);
  };
  // ===== End: Public response dispatcher =====

  return {
    buildOperationalSecurityCrisisFromResponse,
  };
}
